import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, Navigate, Outlet, useLocation } from 'react-router-dom'
import { Sidebar } from './Sidebar'
import { dados } from '@/dados'
import { ROTULOS } from './nav'
import { RotuloRotaProvider, useRotuloAtual } from './RotuloRota'
import { BarraInferior } from './BarraInferior'
import { IconChevron, IconMenu } from '@/components/Icon'
import { Alert } from '@/components/ui/States'
import { Button } from '@/components/ui/Button'
import { SISCOMEX_DESCONECTADO, clearSession } from '@/api/client'
import { useSessao } from '@/lib/SessaoProvider'
import { cn } from '@/lib/cn'

/**
 * Casca do aplicativo: sidebar + topbar + conteúdo.
 *
 * Uma tela por rota, via `<Outlet />` do React Router. Trocar de rota desmonta a
 * anterior automaticamente — acabam as telas sobrepostas, os 5 sistemas de
 * overlay e os 4 `setInterval` que disputavam o DOM no sistema antigo.
 */

export function AppShell() {
  const { pathname } = useLocation()
  const sessao = useSessao()
  const [drawerAberto, setDrawerAberto] = useState(false)
  const [avisoSiscomex, setAvisoSiscomex] = useState(false)

  // Fecha o drawer ao trocar de rota (mobile).
  useEffect(() => setDrawerAberto(false), [pathname])

  // Banner de reconexão: 401 do SISCOMEX NUNCA desloga, só avisa.
  useEffect(() => {
    const h = () => setAvisoSiscomex(true)
    window.addEventListener(SISCOMEX_DESCONECTADO, h)
    return () => window.removeEventListener(SISCOMEX_DESCONECTADO, h)
  }, [])

  // Ambiente ativo no Portal Único (val ↔ prod), para o rótulo permanente na
  // sidebar. `queryClient.clear()` da tela de Configurações refaz esta consulta
  // ao trocar de ambiente, então o rótulo acompanha a troca sem reload.
  const statusSiscomex = useQuery({
    queryKey: ['siscomex-status'],
    queryFn: () =>
      dados.siscomexStatusConexao() as Promise<{ environment?: string | null }>,
    staleTime: 60_000,
  })
  const ambiente = statusSiscomex.data?.environment ?? null

  if (!sessao) return <Navigate to="/login" replace />

  const isAdmin = sessao.is_admin === true || sessao.is_admin === 1
  const usuario = {
    nome: sessao.name || sessao.email || 'Usuário',
    email: sessao.email ?? '',
  }
  const sair = () => {
    clearSession()
    window.location.href = '/login'
  }

  return (
    // O provedor envolve tudo: o breadcrumb no cabeçalho lê o rótulo que a tela
    // publica de dentro do <Outlet/>.
    <RotuloRotaProvider>
      {/*
        UMA barra de rolagem: a do documento.

        A versão anterior usava `h-dvh overflow-hidden` com o `<main>` rolando por
        dentro. Isso criava **duas barras** — a interna, do conteúdo, e a do
        navegador, que ainda sobrava alguns pixels e descia para uma área branca.

        Agora quem rola é a página. A sidebar e o cabeçalho ficam parados por
        `sticky`, sem container de rolagem próprio. Ganhos além de resolver a
        duplicidade: o navegador restaura a posição ao voltar, o Ctrl+F rola até o
        resultado, e no celular a barra de endereço se recolhe como o usuário espera.
      */}
      <div className="app-caixa-alta flex min-h-dvh bg-white">
        {/* -------------------------------------------------- sidebar (desktop) */}
        {/* `sticky` + `h-dvh`: acompanha a rolagem sem sair da tela, e o menu
            interno continua com rolagem própria quando não couber. */}
        <div className="sticky top-0 hidden h-dvh shrink-0 lg:block">
          <Sidebar
            isAdmin={isAdmin}
            siscomexConectado={!avisoSiscomex}
            ambiente={ambiente}
            usuario={usuario}
            onLogout={sair}
          />
        </div>

        {/* --------------------------------------------------- drawer (mobile) */}
        {drawerAberto && (
          <div className="fixed inset-0 z-40 lg:hidden">
            <button
              type="button"
              aria-label="Fechar menu"
              onClick={() => setDrawerAberto(false)}
              className="absolute inset-0 bg-ink-900/25"
            />
            <div className="absolute inset-y-0 left-0 w-60 shadow-md">
              <Sidebar
                isAdmin={isAdmin}
                siscomexConectado={!avisoSiscomex}
                ambiente={ambiente}
                usuario={usuario}
                onLogout={sair}
                onNavigate={() => setDrawerAberto(false)}
              />
            </div>
          </div>
        )}

        {/* ---------------------------------------------------------- conteúdo */}
        <div className="flex min-w-0 flex-1 flex-col">
          {/* Cabeçalho fixo no topo enquanto a página rola. */}
          <header className="sticky top-0 z-30 flex h-14 shrink-0 items-center gap-3 border-b border-ink-200 bg-white px-4">
            <button
              type="button"
              onClick={() => setDrawerAberto(true)}
              aria-label="Abrir menu"
              className="-ml-1 rounded p-1.5 text-ink-500 hover:bg-ink-100 lg:hidden"
            >
              <IconMenu className="size-5" />
            </button>

            <Breadcrumb pathname={pathname} />
          </header>

          {avisoSiscomex && (
            <div className="border-b border-warning-200 bg-warning-50 px-4 py-2">
              <Alert
                tone="warning"
                title="SISCOMEX desconectado"
                action={
                  <Button size="sm" variant="secondary" onClick={() => setAvisoSiscomex(false)}>
                    Reconectar
                  </Button>
                }
              >
                Sua sessão do app continua ativa. Reconecte o certificado para consultar o Portal
                Único.
              </Alert>
            </div>
          )}

          {/* `min-w-0`: sem isso, uma tabela larga estica o flex e cria rolagem
              horizontal na página inteira em vez de ficar dentro da tabela.
              `pb-16 lg:pb-0`: espaço para a barra inferior não cobrir o conteúdo
              — sem isso o último botão da tela fica inalcançável no celular. */}
          <main className="min-w-0 flex-1 bg-ink-50/50 pb-16 lg:pb-0">
            <div className="mx-auto max-w-[1400px] p-4 lg:p-6">
              <Outlet />
            </div>
          </main>
        </div>

        {/* Navegação no celular: um toque em vez de dois. */}
        <BarraInferior onAbrirMenu={() => setDrawerAberto(true)} />
      </div>
    </RotuloRotaProvider>
  )
}

/* -------------------------------------------------------------- breadcrumb */

/** Breadcrumb nas telas profundas — pedido explícito do briefing. */
function Breadcrumb({ pathname }: { pathname: string }) {
  const partes = pathname.split('/').filter(Boolean)
  const publicado = useRotuloAtual()
  if (!partes.length) return null

  /**
   * O último trecho pode ser publicado pela tela: `/importacao/68` exibe
   * "10006/26" em vez de "#68". O id do banco não significa nada para quem usa
   * o sistema — o identificador de negócio é o número do processo.
   */
  const rotulo = (p: string, ultimo: boolean) => {
    if (ultimo && publicado) return publicado
    return ROTULOS[p] ?? (/^\d+$/.test(p) ? `#${p}` : p)
  }

  return (
    <nav aria-label="Trilha de navegação" className="flex min-w-0 items-center gap-1.5 text-sm">
      {partes.map((parte, i) => {
        const href = `/${partes.slice(0, i + 1).join('/')}`
        const ultimo = i === partes.length - 1
        return (
          <span key={href} className="flex min-w-0 items-center gap-1.5">
            {i > 0 && <IconChevron className="size-3 shrink-0 text-ink-300" />}
            {ultimo ? (
              <span className="truncate font-medium text-ink-800" aria-current="page">
                {rotulo(parte, true)}
              </span>
            ) : (
              <Link
                to={href}
                className={cn('truncate text-ink-500 transition-colors hover:text-brand-600')}
              >
                {rotulo(parte, false)}
              </Link>
            )}
          </span>
        )
      })}
    </nav>
  )
}

/** Cabeçalho de página padrão. */
export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string
  description?: string
  actions?: React.ReactNode
}) {
  return (
    <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <h1 className="text-xl text-ink-900">{title}</h1>
        {description && <p className="mt-0.5 text-sm text-ink-500">{description}</p>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  )
}
